/**
 * @file PresentationChannels.ts
 * @role The choreography vocabulary: which decorative channels exist, what each one can be
 *       told to do, and the exact shape an adapter receives for every command.
 * @owns Channel/command names, the precise per-command step types, the reduced-motion safe
 *       list, and the narrow adapter port every channel implements.
 * @reads Nothing at runtime. Type-only imports keep the vocabulary aligned with the controllers
 *        that ultimately execute it.
 * @neverDoes Validate config, hold state, or import a channel implementation.
 * @invariants 8, 9, 13.
 * @see src/game/presentation/PresentationSequenceConfig.ts, docs/CONFIG_GUIDE.md
 */
import type { ConcreteGraphicsQuality } from '@core/graphics/GraphicsQualityService';
import type { SoundId } from '@core/audio/SoundCatalog';
import type { AmbientTier } from '@game/ambient/AmbientFxService';
import type { NeonTargetSelector } from '@game/ambient/NeonFxController';

/** Decorative channels a sequence may drive. Each maps to exactly one existing owner, reached
 *  through a narrow adapter — the explanatory core (symbols, payline, amount, board dim) stays
 *  with WinPresentationController and is deliberately absent here. */
export const PRESENTATION_CHANNELS = [
  'title',
  'neon',
  'particles',
  'postFx',
  'ambient',
  'audio',
  'featureStatus',
] as const;

export type PresentationChannel = (typeof PRESENTATION_CHANNELS)[number];

/* -------------------------------------------------------------------------- */
/* Command vocabularies                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Commands are grouped by whether they need an accompanying reference. "Simple" commands carry
 * no required field, which is what makes a reduced-motion substitution provably sound: swapping
 * the command can never leave a required field behind.
 */
export const TITLE_COMMANDS = [
  'show',
  'hide',
  'fadeIn',
  'fadeOut',
  'reveal',
  'shakeCascade',
  'shakeScaleCascade',
  'punch',
  'bounce',
  'wave',
  'tilt',
  'glowPulse',
  'stop',
] as const;

export const NEON_SIMPLE_COMMANDS = [
  'show',
  'hide',
  'fadeIn',
  'fadeOut',
  'scalePulse',
  'shake',
  'move',
  'glow',
  'clearTransient',
] as const;

export const NEON_COMMANDS = [
  ...NEON_SIMPLE_COMMANDS,
  'runSequence',
  'setMovementProfile',
] as const;

export const PARTICLE_COMMANDS = ['emit', 'clear'] as const;
export const POST_FX_COMMANDS = [
  'flash',
  'screenShake',
  'bloomPulse',
  'frameHalo',
  'clear',
] as const;
export const AMBIENT_COMMANDS = ['setTier', 'returnToIdle'] as const;
export const AUDIO_COMMANDS = ['play', 'stop'] as const;
export const FEATURE_STATUS_COMMANDS = ['showRemaining', 'pulse', 'reset'] as const;

export type TitleCommand = (typeof TITLE_COMMANDS)[number];
export type NeonCommandName = (typeof NEON_COMMANDS)[number];
export type ParticleCommand = (typeof PARTICLE_COMMANDS)[number];
export type PostFxCommand = (typeof POST_FX_COMMANDS)[number];
export type AmbientCommand = (typeof AMBIENT_COMMANDS)[number];
export type AudioCommand = (typeof AUDIO_COMMANDS)[number];
export type FeatureStatusCommand = (typeof FEATURE_STATUS_COMMANDS)[number];

/** Every command name a channel accepts, for schema construction and boot validation. */
export const PRESENTATION_COMMANDS = {
  title: TITLE_COMMANDS,
  neon: NEON_COMMANDS,
  particles: PARTICLE_COMMANDS,
  postFx: POST_FX_COMMANDS,
  ambient: AMBIENT_COMMANDS,
  audio: AUDIO_COMMANDS,
  featureStatus: FEATURE_STATUS_COMMANDS,
} as const satisfies Readonly<Record<PresentationChannel, readonly [string, ...string[]]>>;

/**
 * Commands that stay meaningful when the player has asked for reduced motion: opacity, tint,
 * restrained glow, audio, and state changes.
 *
 * Two properties are load-bearing and enforced by boot validation. Nothing here translates,
 * rotates, scales, or sprays particles, so a substitution can never smuggle spatial motion back
 * in. And every entry is a command that requires no extra field, so substituting one for another
 * can never produce a step missing a reference it needs to act.
 */
export const REDUCED_MOTION_SAFE_COMMANDS = {
  title: ['show', 'hide', 'fadeIn', 'fadeOut', 'glowPulse', 'stop'],
  neon: ['show', 'hide', 'fadeIn', 'fadeOut', 'glow', 'clearTransient'],
  particles: ['clear'],
  postFx: ['bloomPulse', 'frameHalo', 'clear'],
  ambient: ['returnToIdle'],
  audio: ['play', 'stop'],
  featureStatus: ['showRemaining', 'pulse', 'reset'],
} as const satisfies Readonly<Record<PresentationChannel, readonly string[]>>;

/* -------------------------------------------------------------------------- */
/* Step shapes                                                                 */
/* -------------------------------------------------------------------------- */

export type ReducedMotionPolicy = 'keep' | 'omit' | 'substitute';

/** Timing and policy every step carries, whatever its channel. */
export interface PresentationStepTiming {
  /** Offset from the phase's start. In a serial phase this is extra delay after the previous
   *  step's duration, not an absolute position. */
  readonly atMs: number;
  /** How long the command occupies its channel. Drives serial chaining and the boot-time budget
   *  proof; it does not re-tune the effect, whose own timing lives in the owning config. */
  readonly durationMs: number;
  /** Lowest graphics quality that still runs this step. Absent means every quality runs it. */
  readonly minQuality?: ConcreteGraphicsQuality;
  readonly reducedMotion: ReducedMotionPolicy;
}

/**
 * Per-command step shapes. These are discriminated on `command`, so an adapter that switches on
 * it receives the reference already narrowed to a required field — no non-null assertions, and
 * no way to author `{ command: 'glow', sequenceId: 'shower' }` and have the dead reference
 * boot-validated as a real one.
 */
export type TitleStep = PresentationStepTiming & {
  readonly channel: 'title';
  readonly command: TitleCommand;
  readonly scope: TitleScope;
  readonly animationPreset?: string;
  readonly substituteCommand?: TitleCommand;
};

/** Mirrors GameTitleFxController's own scope vocabulary; it applies scope as sticky state, so a
 *  step always carries the full value rather than relying on what ran before it. */
export type TitleScope = 'all' | 'title' | 'subtitle' | 'letter:0' | 'word:0' | 'word:1';

type NeonCommon = PresentationStepTiming & {
  readonly channel: 'neon';
  readonly selector: NeonTargetSelector;
  readonly substituteCommand?: (typeof NEON_SIMPLE_COMMANDS)[number];
};

export type NeonStep = NeonCommon &
  (
    | { readonly command: (typeof NEON_SIMPLE_COMMANDS)[number]; readonly intensity?: number }
    | { readonly command: 'runSequence'; readonly sequenceId: string }
    | { readonly command: 'setMovementProfile'; readonly movementProfile: string }
  );

/** Named anchor the adapter resolves to a point; config never carries coordinates. */
export type ParticleOrigin = 'winCenter' | 'boardCenter' | 'reelFrame';

export type ParticleStep = PresentationStepTiming & {
  readonly channel: 'particles';
  readonly substituteCommand?: ParticleCommand;
} & (
    | { readonly command: 'emit'; readonly preset: string; readonly origin: ParticleOrigin }
    | { readonly command: 'clear' }
  );

export type PostFxStep = PresentationStepTiming & {
  readonly channel: 'postFx';
  readonly command: PostFxCommand;
  readonly substituteCommand?: PostFxCommand;
};

export type AmbientStep = PresentationStepTiming & {
  readonly channel: 'ambient';
  readonly substituteCommand?: AmbientCommand;
} & (
    | { readonly command: 'setTier'; readonly tier: AmbientTier }
    | { readonly command: 'returnToIdle' }
  );

export type AudioStep = PresentationStepTiming & {
  readonly channel: 'audio';
  readonly command: AudioCommand;
  readonly soundId: SoundId;
  readonly substituteCommand?: AudioCommand;
};

export type FeatureStatusStep = PresentationStepTiming & {
  readonly channel: 'featureStatus';
  readonly command: FeatureStatusCommand;
  readonly intensity?: number;
  readonly substituteCommand?: FeatureStatusCommand;
};

export type PresentationStep =
  | TitleStep
  | NeonStep
  | ParticleStep
  | PostFxStep
  | AmbientStep
  | AudioStep
  | FeatureStatusStep;

/** Maps a channel name to the exact step shape its adapter receives. */
export interface PresentationStepByChannel {
  title: TitleStep;
  neon: NeonStep;
  particles: ParticleStep;
  postFx: PostFxStep;
  ambient: AmbientStep;
  audio: AudioStep;
  featureStatus: FeatureStatusStep;
}

/* -------------------------------------------------------------------------- */
/* Adapter port                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One decorative system, reduced to what choreography needs. Deliberately tiny: the coordinator
 * must not be able to reach past this into a controller's own timing, geometry, or lifecycle.
 *
 * Implementations switch on `step.command` — that switch is a translation table to a differently
 * shaped controller API, not a smell, and the discriminated step types keep it exhaustive.
 */
export interface PresentationChannelAdapter<TStep> {
  /** Apply one already-validated command. Must be finite and reversible by `clearTransient`. */
  run(step: TStep): void;
  /** Restore this channel's canonical resting state WITHOUT dropping base context — a transient
   *  win cleanup must never take the game out of free-spins mode. Base context is re-applied by
   *  the coordinator afterwards, as ordinary steps. */
  clearTransient(): void;
}

export type PresentationChannelAdapters = {
  readonly [K in PresentationChannel]?: PresentationChannelAdapter<PresentationStepByChannel[K]>;
};

/* -------------------------------------------------------------------------- */
/* Quality ranking                                                             */
/* -------------------------------------------------------------------------- */

/** Higher means a richer device. Written as an explicit map because
 *  `ConcreteGraphicsQualitySchema`'s own tuple is ordered high-to-low, so an index would invert
 *  the comparison. A step runs when the active rank meets its `minQuality`. */
const QUALITY_RANK: Readonly<Record<ConcreteGraphicsQuality, number>> = {
  low: 0,
  balanced: 1,
  high: 2,
};

export function qualityRank(quality: ConcreteGraphicsQuality): number {
  return QUALITY_RANK[quality];
}
